<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { __, getActualKey, focusTrap } from '@oat-sa-private/ui-core';
    import { testSessionStatus } from '../../../session/sessionStates.js';
    import { getTestSessionStatusStore } from '../../../testsStateStore.js';
    import { OverlayBox } from '@oat-sa-private/ui-components';
    import AttachmentsList from './AttachmentsList.svelte';

    /**
     * Component is used to show attachments toolbar. Should be mounted in floating toolbar container.
     */

    /**
     * @typedef {Object} Props
     * @property {String} serviceCallId
     * @property {import('./plugin.js').Attachment[]} attachments
     * @property {Boolean} showNewTabLinks
     * @property {Function} onClick
     * @property {Function} onAction
     * @property {Function} onClose
     */
    /** @type {Props} */
    // prettier-ignore
    let {
        serviceCallId,
        attachments,
        showNewTabLinks,
        onClick,
        onAction,
        onClose,
    } = $props();

    // svelte-ignore state_referenced_locally
    const statusStore = getTestSessionStatusStore(serviceCallId);

    const startActions = [];
    const endActions = [
        {
            key: 'close',
            icon: 'remove',
            label: __('Close')
        }
    ];

    /**
     * Keydown handler
     * @param {KeyboardEvent} e event
     */
    function handleKeyDown(e) {
        const pressedKey = getActualKey(e);
        if (pressedKey === 'esc') {
            onClose?.();
        }
    }
</script>

<style>
    .attachments-bar {
        pointer-events: auto;
        position: absolute;
        top: -0.5rem;
        inset-inline-end: 0;

        & :global(.overlay-box-content) {
            background: var(--color-bg-info);
        }

        & :global(ul.attachments-list) {
            margin: 3rem;
            border: var(--border-thin) solid var(--color-gs-light-secondary);
            border-radius: var(--radius-large);
        }
    }
</style>

{#if $statusStore === testSessionStatus.interacting}
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
        class="attachments-bar"
        class:hidden={$statusStore === testSessionStatus.overlay}
        onkeydown={handleKeyDown}
        use:focusTrap>
        <OverlayBox
            {startActions}
            {endActions}
            heading={__('Content (%s)', attachments.length)}
            headingLevel={2}
            inverted={true}
            on:action={e => onAction?.(e.detail)}>
            <AttachmentsList {attachments} {showNewTabLinks} {onClick} {onClose} />
        </OverlayBox>
    </div>
{/if}
